import { ProtobufClient } from 'api/protobuf/protobuf-client';
import { goBackendApi } from 'hooks/api/httpApi';
import useSWR, { mutate } from 'swr';
import swrConfig from 'utils/swrConfig';
import { EcomStorefrontAccessTokenResponseSchema } from '../../pb/responses/v1/ecom_storefront_access_token_response_pb';

export const STOREFRONT_ACCESS_TOKEN_SWR_KEY = 'storefrontAccessToken';

const STOREFRONT_ACCESS_TOKEN_PATH = '/api/v1/storefront-access-token';

/** The current shop's storefront access token; `undefined` when it doesn't have one yet (a 404 or an empty token) */
const getStorefrontAccessToken = (): Promise<string | undefined> =>
    new ProtobufClient(goBackendApi())
        .getRequest(EcomStorefrontAccessTokenResponseSchema, STOREFRONT_ACCESS_TOKEN_PATH)
        .then((response) => response?.accessToken || undefined)
        .catch((error) => {
            if (error.status === 404) return undefined;
            throw error;
        });

/** Creates a storefront access token for the current shop. Acts on the session's shop, so there's no request body */
const createStorefrontAccessToken = (): Promise<string | undefined> =>
    new ProtobufClient(goBackendApi())
        .postRequest(null, EcomStorefrontAccessTokenResponseSchema, STOREFRONT_ACCESS_TOKEN_PATH)
        .then((response) => response?.accessToken || undefined);

/** The shop's storefront access token, created first if the shop doesn't have one yet */
export async function fetchStorefrontAccessToken(): Promise<string | undefined> {
    return (await getStorefrontAccessToken()) ?? createStorefrontAccessToken();
}

/**
 * Makes sure the shop has a storefront access token (on entering the app) and seeds the SWR cache with it, so
 * components mounting later read it instead of firing a second request.
 */
export async function preloadStorefrontAccessToken(): Promise<string | undefined> {
    const accessToken = await fetchStorefrontAccessToken();
    await mutate(STOREFRONT_ACCESS_TOKEN_SWR_KEY, accessToken, { revalidate: false });
    return accessToken;
}

export const useStorefrontAccessToken = () => {
    const { data, error, isLoading } = useSWR<string | undefined>(
        STOREFRONT_ACCESS_TOKEN_SWR_KEY,
        fetchStorefrontAccessToken,
        swrConfig
    );

    return { accessToken: data, error, isLoading };
};

export default useStorefrontAccessToken;
